/**
 * PTO report item — types, the derived status, and entry validation.
 *
 * Everything here is PURE and date-string based (ISO `YYYY-MM-DD`, compared
 * lexicographically) so the exact same rules run in the browser for instant
 * feedback and on the server as the authority. No `Date` arithmetic, so no
 * timezone drift between a supervisor's browser and the API.
 */
/**
 * Hours in a J&J work day.
 *
 * A MULTI-DAY booking must total EXACTLY this many hours per work day in its
 * range — an equality, not a ceiling. See {@link validatePtoEntry} for why.
 * A SINGLE-DAY booking may be anything from 1 up to this.
 */
export const MAX_PTO_HOURS_PER_DAY = 10;
/**
 * Smallest bookable slice of PTO. Decimals are allowed (Michael, 2026-09-02), and
 * quarter-hours are the granularity payroll actually uses -- so 2.25 is valid and
 * 2.3 is not, which keeps stored figures exactly representable and stops a UI
 * rounding artefact becoming a payroll figure.
 */
export const PTO_HOURS_INCREMENT = 0.25;
/**
 * Hours below which a remaining balance counts as "in danger" rather than fine.
 * One full day: once less than a day is left, the next ordinary request cannot be
 * taken in full, which is the point at which somebody needs to know.
 */
export const PTO_LOW_BALANCE_THRESHOLD_HOURS = MAX_PTO_HOURS_PER_DAY;
/**
 * Flag a PTO request against the employee's NetSuite balance (Michael's requirement 2).
 *
 * ⚠️ THIS NEVER BLOCKS. Michael: "I still want the logs to show in WLX since admin
 * still need to approve it." So an over-balance request is recorded and flagged, and
 * an admin decides. Returning a verdict rather than throwing is what keeps that
 * possible -- a validation issue would stop the booking being entered at all.
 *
 * PENDING REQUESTS COUNT. Three unapproved requests of 10 hours each against a
 * 20-hour balance each fit individually and cannot all be granted, so the projection
 * is remaining - (pending + this request). Assessing a request in isolation is how a
 * balance goes negative one approval at a time.
 *
 * @param remainingHours from NetSuite; null when unknown.
 * @param requestedHours hours in the booking being assessed.
 * @param pendingHours hours in this employee's OTHER not-yet-approved bookings.
 */
export function assessPtoBalance(remainingHours, requestedHours, pendingHours = 0) {
    if (remainingHours === null || !Number.isFinite(remainingHours)) {
        return {
            verdict: 'unknown',
            projectedRemainingHours: null,
            message: 'PTO balance could not be read from NetSuite — approve only after checking the ' +
                'employee’s accrued time there.',
        };
    }
    const committed = (Number.isFinite(pendingHours) ? pendingHours : 0) + requestedHours;
    const projected = Math.round((remainingHours - committed) * 100) / 100;
    if (projected < 0) {
        return {
            verdict: 'insufficient',
            projectedRemainingHours: projected,
            message: `Not enough PTO: ${String(remainingHours)} h remaining but ${String(committed)} h ` +
                `committed (this request plus ${String(pendingHours)} h awaiting approval) — ` +
                `short by ${String(Math.abs(projected))} h.`,
        };
    }
    if (projected < PTO_LOW_BALANCE_THRESHOLD_HOURS) {
        return {
            verdict: 'low',
            projectedRemainingHours: projected,
            message: `Low PTO balance: ${String(projected)} h would remain after this request — ` +
                `less than a full day.`,
        };
    }
    return {
        verdict: 'ok',
        projectedRemainingHours: projected,
        message: `${String(projected)} h of PTO would remain after this request.`,
    };
}
/**
 * Every day of an inclusive PTO range, weekends INCLUDED (Michael, 2026-09-02).
 *
 * ⚠️ This REVERSES the 2026-08-19 rule that a PTO range covered only Mon-Fri. J&J
 * runs at the weekend, so a Saturday inside a booking is time off like any other day.
 *
 * PTO therefore no longer uses {@link listWorkDays}, which still means Mon-Fri and is
 * still correct for COMPANY HOLIDAYS -- a holiday falling on a Sunday is not a paid
 * day off. The two rules are genuinely different, so they are two functions rather
 * than one function with a flag; a shared helper would invite changing both at once.
 */
export function listPtoDays(startDate, endDate) {
    const total = spanDays(startDate, endDate);
    if (total <= 0) {
        return [];
    }
    const startMs = Date.UTC(Number(startDate.slice(0, 4)), Number(startDate.slice(5, 7)) - 1, Number(startDate.slice(8, 10)));
    const days = [];
    for (let i = 0; i < total; i += 1) {
        days.push(new Date(startMs + i * 86_400_000).toISOString().slice(0, 10));
    }
    return days;
}
/** How many days an inclusive PTO range covers. Weekends included. */
export function countPtoDays(startDate, endDate) {
    return Math.max(0, spanDays(startDate, endDate));
}
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
/** True when `value` is a well-formed ISO `YYYY-MM-DD` calendar date. */
export function isIsoDate(value) {
    if (!ISO_DATE.test(value)) {
        return false;
    }
    // Reject impossible calendar dates (e.g. 2026-02-31) without timezone maths.
    const [y, m, d] = value.split('-').map((part) => Number.parseInt(part, 10));
    if (m < 1 || m > 12 || d < 1) {
        return false;
    }
    const leap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
    const lengths = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return d <= lengths[m - 1];
}
/** Inclusive day count of a range. `2026-08-03..2026-08-03` is 1. */
export function spanDays(startDate, endDate) {
    const start = Date.UTC(Number(startDate.slice(0, 4)), Number(startDate.slice(5, 7)) - 1, Number(startDate.slice(8, 10)));
    const end = Date.UTC(Number(endDate.slice(0, 4)), Number(endDate.slice(5, 7)) - 1, Number(endDate.slice(8, 10)));
    return Math.floor((end - start) / 86_400_000) + 1;
}
/**
 * Work days (Monday–Friday) in an inclusive range.
 *
 * The J&J work week is Mon–Fri (Michael, 2026-08-19), so a weekend inside a
 * booking contributes no PTO hours: Mon 24 Aug → Mon 31 Aug is 8 calendar days
 * but 6 work days, and therefore 60 hours rather than 80.
 *
 * Counted arithmetically rather than by walking the range, and entirely in UTC,
 * so it stays pure and cannot drift with the caller's timezone.
 */
export function countWorkDays(startDate, endDate) {
    const total = spanDays(startDate, endDate);
    if (total <= 0) {
        return 0;
    }
    const startDow = new Date(Date.UTC(Number(startDate.slice(0, 4)), Number(startDate.slice(5, 7)) - 1, Number(startDate.slice(8, 10)))).getUTCDay();
    // Every whole week contributes exactly 5; only the remainder needs checking.
    let count = Math.floor(total / 7) * 5;
    for (let i = 0; i < total % 7; i += 1) {
        const dow = (startDow + i) % 7;
        if (dow !== 0 && dow !== 6) {
            count += 1;
        }
    }
    return count;
}
/**
 * The work days (Mon–Fri) of an inclusive range, as ISO `YYYY-MM-DD` strings in
 * order. The list form of {@link countWorkDays} — used to allocate a booking's
 * hours day by day, which is what makes reconciliation against clocked time
 * possible at all.
 */
export function listWorkDays(startDate, endDate) {
    const total = spanDays(startDate, endDate);
    if (total <= 0) {
        return [];
    }
    const startMs = Date.UTC(Number(startDate.slice(0, 4)), Number(startDate.slice(5, 7)) - 1, Number(startDate.slice(8, 10)));
    const days = [];
    for (let i = 0; i < total; i += 1) {
        const day = new Date(startMs + i * 86_400_000);
        const dow = day.getUTCDay();
        if (dow !== 0 && dow !== 6) {
            days.push(day.toISOString().slice(0, 10));
        }
    }
    return days;
}
/**
 * Derive the lifecycle status from the dates.
 *
 * Before the range → `pending`; on or between the endpoints (inclusive) →
 * `active`; after it → `complete`. `today` is passed in (ISO date) rather than
 * read from the clock so this stays pure and testable.
 */
export function derivePtoStatus(startDate, endDate, today) {
    if (today < startDate) {
        return 'pending';
    }
    if (today > endDate) {
        return 'complete';
    }
    return 'active';
}
/** Human label for a status. */
export function ptoStatusLabel(status) {
    switch (status) {
        case 'new':
            return 'New';
        case 'pending':
            return 'Pending';
        case 'active':
            return 'Active';
        case 'complete':
            return 'Complete';
        /* c8 ignore next 2 */
        default:
            return status;
    }
}
/**
 * Validate a PTO entry. Returns EVERY broken rule, not just the first, so the
 * Flag column can tell a supervisor everything to fix in one pass.
 *
 * These are the STRUCTURAL rules — the ones that must hold for a row to make
 * sense at all. The finer policy rules (accrual balances, notice periods,
 * per-department limits) are deliberately not here yet; add them to this
 * function and both the UI and the API pick them up with no other change.
 *
 * THE HOURS RULE (Michael, 2026-08-19):
 *   - a MULTI-DAY range must total EXACTLY 10 hours per WORK DAY (Mon–Fri) in
 *     it — an equality, because a range carries no per-day breakdown and an
 *     uneven total would be split evenly at posting time, falsifying which day
 *     the hours belonged to;
 *   - a partial day is therefore recorded as its OWN single-day entry;
 *   - a SINGLE-day entry may be 1 to 10 hours.
 */
export function validatePtoEntry(input, context) {
    const issues = [];
    if (input.employeeId === '') {
        issues.push({ field: 'employee', message: 'Pick an employee.' });
    }
    else if (!context.employeeIds.has(input.employeeId)) {
        issues.push({
            field: 'employee',
            message: 'That employee is not an active hourly employee — pick someone from the list.',
        });
    }
    if (input.departmentId === '') {
        issues.push({ field: 'department', message: 'Pick a department.' });
    }
    else if (!context.departmentIds.has(input.departmentId)) {
        issues.push({
            field: 'department',
            message: 'That department is not active — pick one from the list.',
        });
    }
    const startOk = isIsoDate(input.startDate);
    const endOk = isIsoDate(input.endDate);
    if (!startOk) {
        issues.push({ field: 'startDate', message: 'Pick a start date.' });
    }
    if (!endOk) {
        issues.push({ field: 'endDate', message: 'Pick an end date.' });
    }
    if (startOk && endOk && input.endDate < input.startDate) {
        issues.push({
            field: 'endDate',
            message: `End date is before the start date — set it to ${input.startDate} or later.`,
        });
    }
    /*
     * NO FUTURE-DATES RULE. Removed 2026-09-17 on Michael's instruction: "The PTO entry
     * rule that states an entry can not be entered with a past date needs to be removed.
     * We have a lot of supers that wait until the end of the week to put in PTO requests
     * for dates that happened in that week."
     *
     * It used to refuse any NEW booking starting on or before `context.today`, which
     * made the ordinary case — writing up Monday's sick day on Friday — impossible
     * without an administrator. The rule described an approval workflow WLX does not
     * have: nothing here reaches pay until a human posts it, and the overlap, hours and
     * balance checks below are what actually protect the figures.
     *
     * `context.today` is still required, and is still used by the callers for the
     * standing/balance view; it is simply no longer a boundary on the start date.
     */
    // Decimals are allowed (Michael, 2026-09-02), but only on the quarter hour. An
    // unconstrained decimal lets a UI rounding artefact (2.9999999) become a payroll
    // figure, and 0.25 is the granularity payroll actually works in.
    //
    // The multiplied comparison avoids the modulo: `2.4 % 0.25` is 0.24999999999999978
    // in binary floating point, so a plain `!== 0` check would reject valid values.
    if (!Number.isFinite(input.hours)) {
        issues.push({ field: 'hours', message: 'Hours must be a number.' });
    }
    else if (Math.abs(Math.round(input.hours / PTO_HOURS_INCREMENT) * PTO_HOURS_INCREMENT - input.hours) > 1e-9) {
        issues.push({
            field: 'hours',
            message: `Hours must be in increments of ${String(PTO_HOURS_INCREMENT)} — for example 4, 4.25, 4.5 or 4.75.`,
        });
    }
    else if (input.hours <= 0) {
        issues.push({ field: 'hours', message: 'Hours must be greater than zero.' });
    }
    else if (startOk && endOk && input.endDate >= input.startDate) {
        if (input.startDate === input.endDate) {
            // A SINGLE day may be partial — that is exactly how a partial day is meant
            // to be recorded, so anything from 1 up to a full work day is valid.
            if (input.hours > MAX_PTO_HOURS_PER_DAY) {
                issues.push({
                    field: 'hours',
                    message: `A single day cannot exceed ${String(MAX_PTO_HOURS_PER_DAY)} hours — ` +
                        `reduce the hours, or extend the end date if the time off spans more than one day.`,
                });
            }
        }
        else {
            // ALL days, weekends included -- see listPtoDays.
            const ptoDays = countPtoDays(input.startDate, input.endDate);
            if (ptoDays === 0) {
                issues.push({
                    field: 'entry',
                    message: 'This range covers no days — check the start and end dates.',
                });
            }
            else {
                const required = ptoDays * MAX_PTO_HOURS_PER_DAY;
                if (input.hours !== required) {
                    // EXACT, not a ceiling. A multi-day booking carries no per-day detail,
                    // so anything other than full days would have to be spread evenly
                    // across the range at posting time — inventing an allocation nobody
                    // entered and misstating which day the time actually came from.
                    // Partial days are therefore recorded as their own single-day rows.
                    issues.push({
                        field: 'hours',
                        message: `A ${String(ptoDays)}-day range must total exactly ${String(required)} hours ` +
                            `(${String(ptoDays)} × ${String(MAX_PTO_HOURS_PER_DAY)}), but ${String(input.hours)} ` +
                            `${input.hours === 1 ? 'was' : 'were'} entered. Enter ${String(required)}, or if any ` +
                            `day is a partial day, book that day as its own single-day entry so the hours land ` +
                            `on the right date.`,
                    });
                }
            }
        }
    }
    if (startOk && endOk && input.endDate >= input.startDate && input.employeeId !== '') {
        const clash = context.existing.find((row) => row.id !== context.editingId &&
            row.employeeId === input.employeeId &&
            row.startDate <= input.endDate &&
            row.endDate >= input.startDate);
        if (clash !== undefined) {
            issues.push({
                field: 'entry',
                message: `This employee already has PTO booked ${clash.startDate} to ${clash.endDate} — change the dates or edit that entry instead.`,
            });
        }
    }
    return issues;
}
/**
 * Measure a booking against clocked time.
 *
 * THE RULE (Michael, 2026-08-19): worked hours take precedence over PTO. A day
 * totals at most {@link MAX_PTO_HOURS_PER_DAY} hours, so PTO on a day is cut to
 * whatever the clock left unfilled, and 10+ hours worked leaves none. The result
 * may be fractional even though bookings are entered in whole hours — that is
 * expected, and is why nothing here is rounded to an integer.
 *
 * `workedHoursByDate` maps ISO `YYYY-MM-DD` → hours clocked that day (already
 * summed across departments and rounded by the tenant's payroll rounding rule,
 * so these figures agree with what posts to NetSuite).
 *
 * Pure, so the browser and the API produce identical numbers.
 */
export function reconcilePtoEntry(entry, workedHoursByDate) {
    const ptoDays = listPtoDays(entry.startDate, entry.endDate);
    const isSingleDay = entry.startDate === entry.endDate;
    /**
     * COERCE BEFORE COMPARING. `hours` is typed `number`, but a Postgres `numeric`
     * arrives from node-pg as the STRING '20.00', and the multi-day branch below
     * compares with `===`. A string can never be strictly equal to a number, so
     * every multi-day booking read from the database was flagged unallocatable —
     * observed in production 2026-09-13 on a genuinely correct 2-day / 20-hour
     * entry. The single-day branch used `<=`, which coerces, which is exactly why
     * only multi-day rows broke and the bug survived this long.
     *
     * The caller is fixed too (pto.service.ts parses at the row boundary); this
     * stays because a silent wrong answer in a pure payroll function is worse than
     * a redundant coercion, and this function has other callers.
     */
    const hours = Number(entry.hours);
    if (!Number.isFinite(hours)) {
        return {
            days: [],
            bookedHours: 0,
            effectiveHours: 0,
            reducedHours: 0,
            blockedDates: [],
            unallocatable: true,
        };
    }
    // A multi-day booking allocates a FULL day to every day in the range — guaranteed
    // by the hours rule, which is what makes this attribution honest rather than an
    // even split of an arbitrary total. Weekends included (Michael, 2026-09-02).
    const conforming = isSingleDay
        ? hours <= MAX_PTO_HOURS_PER_DAY
        : hours === ptoDays.length * MAX_PTO_HOURS_PER_DAY;
    if (!conforming || ptoDays.length === 0) {
        return {
            days: [],
            bookedHours: hours,
            effectiveHours: hours,
            reducedHours: 0,
            blockedDates: [],
            unallocatable: true,
        };
    }
    const days = ptoDays.map((date) => {
        const bookedHours = isSingleDay ? hours : MAX_PTO_HOURS_PER_DAY;
        const workedHours = workedHoursByDate[date] ?? 0;
        // Only ever reduces: a short day of work does not top a booking up.
        const remaining = Math.max(0, MAX_PTO_HOURS_PER_DAY - workedHours);
        return {
            date,
            bookedHours,
            workedHours,
            effectiveHours: Math.min(bookedHours, remaining),
        };
    });
    const effectiveHours = round2(days.reduce((sum, d) => sum + d.effectiveHours, 0));
    return {
        days,
        bookedHours: hours,
        effectiveHours,
        reducedHours: round2(hours - effectiveHours),
        blockedDates: days.filter((d) => d.effectiveHours === 0).map((d) => d.date),
        unallocatable: false,
    };
}
/** Two-decimal rounding, so floating-point noise never reaches an hours figure. */
function round2(value) {
    return Math.round(value * 100) / 100;
}
//# sourceMappingURL=pto.js.map